/**
 * Pure rules for the member profile page, shared by the staff and member views.
 * No React: everything here is plain data in, plain data out, so it is easy to test.
 */
import { formatNaira } from "../../utils/dashboardFormat";

const num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/** ₦9.8M, ₦1.49M, ₦15,400. Short enough for a tile; the full amount goes in a tooltip. */
export function compactNaira(amount) {
  const n = num(amount);
  const abs = Math.abs(n);
  const trim = (v) => String(Number(v.toFixed(2)));
  if (abs >= 1e9) return `₦${trim(n / 1e9)}B`;
  if (abs >= 1e6) return `₦${trim(n / 1e6)}M`;
  return formatNaira(n);
}

/** Loans arrive grouped by status; the table wants one list, newest first. */
export function flattenLoans(breakdown) {
  const groups = breakdown && typeof breakdown === "object" ? breakdown : {};
  const rows = [];
  Object.keys(groups).forEach((key) => {
    (Array.isArray(groups[key]) ? groups[key] : []).forEach((loan) => {
      rows.push({
        id: loan.id,
        reference: loan.reference || "",
        type: typeof loan.loan_type === "string" ? loan.loan_type : loan.loan_type?.name || "Loan",
        amount: num(loan.amount),
        status: String(loan.status || key).toLowerCase(),
        createdAt: loan.created_at || null,
        repaymentDate: loan.repayment_date || null,
        duration: num(loan.duration),
        durationType: loan.duration_type || "days",
      });
    });
  });
  return rows.sort((a, b) => String(b.createdAt || "").localeCompare(String(a.createdAt || "")));
}

/** Repayment instalments come grouped as overdue / pending / paid. Overdue first, then by date. */
export function flattenRepayments(breakdown) {
  const groups = breakdown && typeof breakdown === "object" ? breakdown : {};
  const order = ["overdue", "pending", "paid"];
  const rows = [];
  order.forEach((key) => {
    (Array.isArray(groups[key]) ? groups[key] : []).forEach((item) => {
      rows.push({
        id: item.id,
        loanType: typeof item.loan_type === "string" ? item.loan_type : item.loan_type?.name || "Loan",
        amount: num(item.amount),
        interest: num(item.interest),
        total: num(item.total_amount ?? num(item.amount) + num(item.interest)),
        dueAt: item.due_at || null,
        paidAt: item.paid_at || null,
        status: key,
        daysOverdue: num(item.days_overdue),
        method: item.payment_method
          ? String(typeof item.payment_method === "string" ? item.payment_method : item.payment_method.name || "").replace(/_/g, " ")
          : "",
      });
    });
  });
  return rows;
}

const countOf = (list) => (Array.isArray(list) ? list.length : 0);
const countGroups = (groups) =>
  groups && typeof groups === "object"
    ? Object.values(groups).reduce((sum, list) => sum + countOf(list), 0)
    : 0;

/**
 * "Nothing recorded" and "we could not load it" must never look the same.
 *  - rows present        -> "ready"
 *  - no rows, total is 0 -> "empty"   (a genuinely empty tab)
 *  - no rows, total > 0  -> "missing" (the page has the total but not the records behind it)
 */
export function sectionState({ rows, total }) {
  if (rows > 0) return "ready";
  return num(total) > 0 ? "missing" : "empty";
}

/** The numbers the money strip and the tab badges are built from. */
export function buildProfileSummary(details = {}) {
  const d = details || {};
  const contributions = num(d.contributions?.total ?? d.total_contribution);
  const savings = num(d.savings?.total);
  const shares = num(d.shares?.total ?? d.total_shares);
  const loanTotals = d.loans?.totals_by_status || {};
  const overdue = num(d.repayment_history?.total_overdue ?? d.overdue_repayment);

  const loanRows = flattenLoans(d.loans?.breakdown);
  const loanTotal = num(d.loans?.total ?? d.loanTaken);
  const loanCount = loanRows.length || countGroups(d.loans?.breakdown);

  const apiMultiplier = num(d.borrowing_multiplier);
  const hasMultiplier = apiMultiplier > 0;
  const apiPower = d.maximum_borrowing_power;
  const borrowingPower =
    apiPower != null && Number.isFinite(Number(apiPower))
      ? Number(apiPower)
      : hasMultiplier
        ? (savings + shares) * apiMultiplier
        : null;

  const counts = {
    loans: loanCount,
    repayments: countGroups(d.repayment_history?.breakdown),
    savings: countOf(d.savings?.breakdown),
    shares: countOf(d.shares?.breakdown),
    contributions: countOf(d.contributions?.breakdown),
  };

  return {
    contributions,
    savings,
    shares,
    loanTotal,
    activeLoanBalance: num(d.current_loan ?? loanTotals.disbursed),
    overdue,
    pendingRepayment: num(d.repayment_history?.total_pending),
    paidRepayment: num(d.repayment_history?.total_paid),
    pendingLoanRequests: num(loanTotals.pending),
    pendingLoanCount: countOf(d.loans?.breakdown?.pending),
    borrowingPower,
    multiplierLabel: hasMultiplier ? `${Number(apiMultiplier.toFixed(2))}x` : null,
    counts,
    overdueRows: flattenRepayments(d.repayment_history?.breakdown).filter((r) => r.status === "overdue"),
  };
}
